import { type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PortalStatCardProps {
  icon: LucideIcon
  label: string
  value: ReactNodeLike
  hint?: string
  tone?: 'default' | 'success' | 'warning' | 'destructive' | 'accent'
  className?: string
}

type ReactNodeLike = string | number | React.ReactNode

const toneClasses: Record<NonNullable<PortalStatCardProps['tone']>, string> = {
  default: 'bg-primary/10 text-primary',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  destructive: 'bg-destructive/10 text-destructive',
  accent: 'bg-accent/15 text-accent',
}

/** Compact stat tile used in stat strips (e.g. Portal dashboard). */
export function PortalStatCard({ icon: Icon, label, value, hint, tone = 'default', className }: PortalStatCardProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card p-4 flex items-start gap-3 hover:shadow-md transition-shadow',
        className
      )}
    >
      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', toneClasses[tone])}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        <p className="text-lg font-semibold text-foreground leading-tight truncate">{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-0.5 truncate">{hint}</p>}
      </div>
    </div>
  )
}
